/* eslint-disable no-restricted-imports */
/* eslint-disable complexity */
/* eslint-disable functional/no-loop-statement */
import { Box, Typography, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { FC, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { DpScrollContainer } from '@/components/blocks/ScrollContainer';
import { Grid, IconButton } from '@mui/material';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import { CatalogEntryCarouselSeeMoreContext } from '@/data/context/catalogEntryCarouselSeeMore';
import { Linkable } from '@/components/blocks/Linkable';
import { formattedCatalogEntryCarouselId } from 'integration/utils/formattedCatalogEntryCarouselId';
import { isCatalogEntryCarouselDefaultHidden } from 'integration/utils/isCatalogEntryCarouselDefaultHidden';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { useCentralContainerRemainingSpace } from 'integration/hooks/useCentralContainerRemainingSpace';

export const CatalogEntryCarousel: FC<{
	id: string;
	slides: JSX.Element[];
}> = ({ id, slides }) => {
	const [showScrollButton, setShowScrollButton] = useState(true);
	const scrollContainerRef = useRef<HTMLDivElement>(null);
	const [isEndReached, setIsEndReached] = useState<boolean>(false);
	const [positionActive, setPositionActive] = useState<number>(310);

	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
	const spacing = useCentralContainerRemainingSpace();
	const [startPosition, setStartPosition] = useState(0);
	const [scrollPosition, setScrollPosition] = useState(0);

	const positions = useMemo(() => {
		const positionsTemp = [];
		for (let i = 0; i < slides.length; i++) {
			positionsTemp.push(i * 310);
		}
		return positionsTemp;
	}, [slides.length]);

	useEffect(() => {
		const scrollContainer = scrollContainerRef.current;

		const handleScroll = () => {
			if (!scrollContainer) return;

			const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
			const isEnd = scrollLeft + clientWidth >= scrollWidth;
			const responses = positions.filter((e) => e > scrollLeft);
			setPositionActive(responses[0] || positions[positions.length]);
			setIsEndReached(isEnd);
			setShowScrollButton(!isEnd);
		};

		scrollContainer?.addEventListener('scroll', handleScroll);

		return () => {
			scrollContainer?.removeEventListener('scroll', handleScroll);
		};
	}, [positions, slides.length]);

	const handleScrollRight = () => {
		if (scrollContainerRef.current) {
			scrollContainerRef.current.scrollTo({
				left: positionActive,
				behavior: 'smooth',
			});
		}
	};

	const handleScrollLeft = () => {
		if (scrollContainerRef.current) {
			scrollContainerRef.current.scrollTo({
				left: positionActive - (isEndReached ? 310 : 620),
				behavior: 'smooth',
			});
		}
	};
	const [isDragging, setIsDragging] = useState(false);

	const handleMouseDown = (e: MouseEvent) => {
		setIsDragging(true);
		setStartPosition(e.clientX);
		setScrollPosition(scrollContainerRef.current?.scrollLeft || 0);
	};

	const handleMouseMove = (e: MouseEvent) => {
		if (isDragging && scrollContainerRef.current) {
			const distance = e.clientX - startPosition;
			scrollContainerRef.current.scrollLeft = scrollPosition - distance;
		}
	};

	const handleMouseUp = () => {
		setIsDragging(false);
	};

	useEffect(() => {
		const container = scrollContainerRef.current;
		if (container) {
			container.addEventListener('mousedown', handleMouseDown);
			container.addEventListener('mousemove', handleMouseMove);
			container.addEventListener('mouseup', handleMouseUp);
			container.addEventListener('mouseleave', handleMouseUp);
		}

		return () => {
			if (container) {
				container.removeEventListener('mousedown', handleMouseDown);
				container.removeEventListener('mousemove', handleMouseMove);
				container.removeEventListener('mouseup', handleMouseUp);
				container.removeEventListener('mouseleave', handleMouseUp);
			}
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isDragging, startPosition, scrollPosition]);

	const { entryCarouselSeeMore } = useContext(CatalogEntryCarouselSeeMoreContext);

	const entryCarouselSeeMoreId = Object.entries(entryCarouselSeeMore).find(([key]) => key === id);
	return (
		<Grid
			id={formattedCatalogEntryCarouselId(id)}
			position="relative"
			display={isCatalogEntryCarouselDefaultHidden(id) ? 'none' : 'grid'}
		>
			{!isMobile && positionActive - (isEndReached ? 0 : 310) > 0 ? (
				<IconButton
					onClick={handleScrollLeft}
					sx={{
						height: '38px',
						width: '38px',
						position: 'absolute',
						top: '45%',
						left: '20px',
						marginLeft: `calc(${spacing})`,
						backgroundColor: '#F1F1F1',
						':hover': { backgroundColor: '#F1F1F1' },
						zIndex: 999,
						boxShadow: '0px 0px 10px rgba(0, 0, 0, 0.1)',
					}}
				>
					<ChevronLeft sx={{ color: 'text.primary' }} />
				</IconButton>
			) : null}
			<DpScrollContainer
				sx={{
					display: 'flex',
					flexDirection: 'row',
					gap: '0px',
					position: 'relative',
					cursor: isDragging ? 'grabbing' : 'grab',
					userSelect: isDragging ? 'none' : 'none',
				}}
				ref={scrollContainerRef}
			>
				{slides.map((slide, index) => (
					<Box
						key={`id_see_you_recent_${index}`}
						sx={{
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
							marginRight: index === slides.length - 1 ? '0px' : { xs: '5px', sm: '20px' },
						}}
					>
						{slide}
					</Box>
				))}
				{entryCarouselSeeMoreId ? (
					<Linkable href={entryCarouselSeeMoreId[1]}>
						<Box
							sx={{
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
								flexDirection: 'column',
								marginLeft: { xs: '5px', sm: '20px' },
								position: 'relative',
							}}
						>
							<Box
								sx={{
									width: '100%',
									height: '100%',
									backgroundColor: 'rgba(0, 0, 0, 0.5)',
									position: 'absolute',
									top: 0,
									left: 0,
									display: 'flex',
									flexDirection: 'column',
									alignItems: 'center',
									justifyContent: 'center',
									padding: { xs: '25px', sm: '50px' },
									zIndex: 998,
								}}
							>
								<Typography
									sx={{
										fontSize: { xs: 'var(--font-size-md)', sm: 'var(--font-size-lg)' },
										color: 'white',
										fontWeight: 'bold',
										textAlign: 'center',
										marginBottom: '10px',
									}}
								>
									{'VER MÁS PRODUCTOS'}
								</Typography>
								<MoreHorizIcon sx={{ color: 'white', fontSize: 'var(--font-size-display)' }} />
							</Box>
							{slides[0]}
						</Box>
					</Linkable>
				) : null}
			</DpScrollContainer>
			{!isMobile && !isEndReached && showScrollButton ? (
				<IconButton
					onClick={handleScrollRight}
					sx={{
						height: '38px',
						width: '38px',
						position: 'absolute',
						top: '45%',
						right: '48px',
						backgroundColor: '#F1F1F1',
						':hover': { backgroundColor: '#F1F1F1' },
						zIndex: 999,
						boxShadow: '0px 0px 10px rgba(0, 0, 0, 0.1)',
					}}
				>
					<ChevronRight sx={{ color: 'text.primary' }} />
				</IconButton>
			) : null}
		</Grid>
	);
};
